import React, {ReactElement, ReactNode} from "react";

// @ts-expect-error
import mapboxgl, {MapMouseEvent} from "!mapbox-gl";

import "mapbox-gl/dist/mapbox-gl.css";

import ModuleBody from "../layouts/module-body/ModuleBody";
import Icon from "../icon/Icon";

import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";
import {MaybeType} from "/src/types";

import MapBoxIcon from "/src/svg/mapbox-flexport-office-marker.svg";

import * as styles from "./global-reach-map.module.scss";

const GATSBY_MAPBOX_GL_ACCESS_TOKEN =
  process.env.GATSBY_MAPBOX_GL_ACCESS_TOKEN!;

const CONFIG_DEFAULTS = {
  Latitude: 24.561916,
  Longitude: 79.403231,
  Style: "mapbox://styles/flex-tuan/clj56fc0i001601r24hiscet7",
  Zoom: 2,
};

const GlobalReachMap = (
  props: Queries.GlobalReachMapFragmentFragment
): ReactElement | null => {
  const {centerLatitude, centerLongitude, style, title, zoom} = props;

  const $map = React.useRef<HTMLDivElement | null>(null);
  const $mapWrapper = React.useRef<HTMLDivElement | null>(null);
  const mapRef = React.useRef<mapboxgl.Map | null>();

  const [feature, setFeature] =
    React.useState<MaybeType<mapboxgl.MapboxGeoJSONFeature>>(null);
  const [isMapboxSupported, setIsMapboxSupported] = React.useState(true);

  const onMapLoaded = () => {
    $mapWrapper.current?.style.setProperty(
      "visibility",
      "visible",
      "important"
    );
    mapRef.current?.resize();
  };

  const onMapClick = (event: MapMouseEvent) => {
    if (mapRef.current == null) {
      return;
    }

    const selectedFeature: MaybeType<mapboxgl.MapboxGeoJSONFeature> =
      mapRef.current
        .queryRenderedFeatures(event.point, {
          layers: ["flexport-global-office-locations"],
        })
        .shift();

    if (selectedFeature == null) {
      return;
    }

    setFeature(selectedFeature);
  };

  const onPopupClose = () => {
    setFeature(null);
  };

  React.useEffect(() => {
    if ($map.current == null) {
      return;
    }

    mapboxgl.accessToken = GATSBY_MAPBOX_GL_ACCESS_TOKEN;

    if (!mapboxgl.supported()) {
      setIsMapboxSupported(false);
      return;
    }

    mapRef.current = new mapboxgl.Map({
      container: $map.current,
      cooperativeGestures: true,
      center: [
        centerLongitude != null ? centerLongitude : CONFIG_DEFAULTS.Longitude,
        centerLatitude != null ? centerLatitude : CONFIG_DEFAULTS.Latitude,
      ],
      style: style || CONFIG_DEFAULTS.Style,
      zoom: zoom != null ? zoom : CONFIG_DEFAULTS.Zoom,
    });

    mapRef.current?.on("load", onMapLoaded);
    mapRef.current?.on("click", onMapClick);
    mapRef.current?.scrollZoom.disable();
    mapRef.current?.addControl(
      new mapboxgl.NavigationControl({showCompass: false})
    );

    return () => {
      mapRef.current?.remove();
    };
  }, [centerLatitude, centerLongitude, style, zoom]);

  if (!isMapboxSupported) {
    return null;
  }

  return (
    <section className={styles.globalReachMap}>
      {title && (
        <ModuleBody>
          <h2 className={css("h2", styles.globalReachMap__title)}>{title}</h2>
        </ModuleBody>
      )}

      {feature && (
        <GlobalReachMapPopUp
          coordinates={feature.geometry.coordinates}
          map={mapRef.current}
          onClose={onPopupClose}
        >
          <div className={css(styles.globalReachMap__popup)}>
            {feature.properties.name && (
              <h3 className={css("h4", styles.globalReachMap__popup__title)}>
                {feature.properties.name}
              </h3>
            )}

            {feature.properties.address && (
              <p className={css("caption")}>{feature.properties.address}</p>
            )}

            {(feature.properties.phone || feature.properties.email) && (
              <div>
                {feature.properties.phone && (
                  <p className={css("caption")}>{feature.properties.phone}</p>
                )}

                {feature.properties.email && (
                  <div className={css("caption")}>
                    <a href={`mailto:${feature.properties.email}`}>
                      {feature.properties.email}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </GlobalReachMapPopUp>
      )}

      <div ref={$mapWrapper} className={css(styles.globalReachMap__mapWrapper)}>
        <div ref={$map} className={css(styles.globalReachMap__map)} />
      </div>

      <ModuleBody classes={styles.globalReachMap__legendWrapper}>
        <ul className={css(styles.globalReachMap__legendList)}>
          <li className={css(styles.globalReachMap__legendItem)}>
            <Icon
              svg={MapBoxIcon}
              role="presentation"
              classes={styles.globalReachMap__mapIcon}
            />

            <span
              className={css("caption", styles.globalReachMap__legendCaption)}
            >
              {translate("Flexport Office")}
            </span>
          </li>

          <li className={css(styles.globalReachMap__legendItem)}>
            <div className={css(styles.globalReachMap__legendMarker)} />

            <span
              className={css("caption", styles.globalReachMap__legendCaption)}
            >
              {translate("Flexport Coverage")}
            </span>
          </li>
        </ul>
      </ModuleBody>
    </section>
  );
};

export default GlobalReachMap;

interface GlobalReachMapPopUpProp {
  children: ReactNode;
  coordinates: Array<number>;
  map: any;
  onClose: () => void;
}

const GlobalReachMapPopUp = (props: GlobalReachMapPopUpProp): ReactElement => {
  const {children, coordinates, map, onClose} = props;
  const $popup = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const popup = new mapboxgl.Popup({offset: [0, 0]})
      .setLngLat(coordinates)
      .setDOMContent($popup.current)
      .addTo(map);

    popup.on("close", onClose);

    return popup.remove;
  }, [children, coordinates, map, onClose]);

  return (
    <div style={{display: "none"}}>
      <div ref={$popup}>{children}</div>
    </div>
  );
};
